import { forwardRef, useEffect, useState } from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { commitNonNegativeNumber, formatNonNegativeDisplay, parseNonNegativeNumber } from '../../../utils/nonNegativeNumber';

export type NonNegativeNumberFieldProps = Omit<TextFieldProps, 'type' | 'value' | 'onChange'> & {
  value: number | string;
  integer?: boolean;
  min?: number;
  max?: number;
  /** Max number of digits allowed while typing (integer fields). */
  maxDigits?: number;
  decimalPlaces?: number;
  /** When true (default), empty blur commits `0`. */
  commitEmptyAsZero?: boolean;
  onValueChange: (value: number) => void;
  onEmpty?: () => void;
};

const BLOCKED_KEYS = new Set(['-', '+', 'e', 'E']);

const NonNegativeNumberField = forwardRef<HTMLInputElement, NonNegativeNumberFieldProps>(
  function NonNegativeNumberField(
    {
      value,
      integer,
      min,
      max,
      maxDigits,
      decimalPlaces,
      commitEmptyAsZero = true,
      onValueChange,
      onEmpty,
      onFocus,
      onBlur,
      onKeyDown,
      inputRef,
      inputProps,
      ...props
    },
    ref,
  ) {
    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState('');
    const numericValue = Number(value);
    const belowMin = min != null && Number.isFinite(numericValue) && numericValue < min;
    const aboveMax = max != null && Number.isFinite(numericValue) && numericValue > max;
    const committedValue = belowMin ? min : aboveMax ? max : value;

    useEffect(() => {
      if (min == null || focused || !belowMin) return;
      onValueChange(min);
    }, [belowMin, focused, min, onValueChange]);

    useEffect(() => {
      if (max == null || focused || !aboveMax) return;
      onValueChange(max);
    }, [aboveMax, focused, max, onValueChange]);

    const display = focused
      ? draft
      : formatNonNegativeDisplay(committedValue, { integer, decimalPlaces });

    const applyDraft = (raw: string) => {
      const next = parseNonNegativeNumber(raw, { integer, decimalPlaces, maxDigits });
      setDraft(next);
      if (next === '' || next === '.') {
        if (commitEmptyAsZero) onValueChange(min ?? 0);
        else onEmpty?.();
        return;
      }
      onValueChange(commitNonNegativeNumber(next, { integer, min, max, decimalPlaces }));
    };

    return (
      <TextField
        {...props}
        type="text"
        value={display}
        inputRef={inputRef || ref}
        inputProps={{
          inputMode: integer ? 'numeric' : 'decimal',
          min: min ?? 0,
          max,
          pattern: integer ? '[0-9]*' : '[0-9]*[.]?[0-9]*',
          ...inputProps,
        }}
        onFocus={(e) => {
          setFocused(true);
          setDraft(formatNonNegativeDisplay(value, { integer, decimalPlaces }));
          onFocus?.(e);
        }}
        onChange={(e) => applyDraft(e.target.value)}
        onBlur={(e) => {
          setFocused(false);
          if (draft === '' || draft === '.') {
            if (commitEmptyAsZero) {
              onValueChange(min ?? 0);
              setDraft(formatNonNegativeDisplay(min ?? 0, { integer, decimalPlaces }));
            } else {
              onEmpty?.();
              setDraft('');
            }
          } else {
            const committed = commitNonNegativeNumber(draft, { integer, min, max, decimalPlaces });
            onValueChange(committed);
            setDraft(formatNonNegativeDisplay(committed, { integer, decimalPlaces }));
          }
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (BLOCKED_KEYS.has(e.key) || (integer && e.key === '.')) e.preventDefault();
          onKeyDown?.(e);
        }}
      />
    );
  },
);

export default NonNegativeNumberField;
